import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LifeBuoy, ArrowRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlJTIwY2xlYW58ZW58MHx8fHwxNzg2MzM3NTEzfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [loginLogo, setLoginLogo] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/sso/status`)
      .then((r) => r.ok ? r.json() : { enabled: false })
      .then((d) => setSsoEnabled(!!d.enabled))
      .catch(() => setSsoEnabled(false));
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/login-logo`)
      .then((r) => r.ok ? r.json() : { data_url: null })
      .then((d) => setLoginLogo(d.data_url || null))
      .catch(() => setLoginLogo(null));
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("Signed in");
      if (u.sso_provisioned && !u.welcome_seen) {
        nav("/welcome");
      } else {
        const isRequester = u.role === "user" || u.role === "teacher";
        nav(isRequester ? "/tickets" : "/dashboard");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-white dark:bg-zinc-950" data-testid="login-page">
      {/* Left: hero — theme-aware. Image dims slightly in dark mode; overlay + hero text
          swap between light and dark variants so contrast is preserved in both themes. */}
      <div className="relative hidden md:block bg-zinc-100 dark:bg-zinc-950 transition-colors duration-500">
        <img
          src={HERO_IMG}
          alt="Architecture"
          className="absolute inset-0 h-full w-full object-cover opacity-95 dark:opacity-70 brightness-100 dark:brightness-[0.55] transition-[filter,opacity] duration-500"
        />
        {/* Bottom-anchored gradient — light overlay for readability in light mode, dark in dark mode */}
        <div className="absolute inset-0 bg-gradient-to-t from-white/70 via-white/10 to-transparent dark:from-zinc-950/70 dark:via-zinc-950/10 transition-opacity duration-500" />
        <div className="relative z-10 flex flex-col justify-between h-full p-12 text-zinc-900 dark:text-zinc-50">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-md bg-zinc-950 text-white dark:bg-white dark:text-zinc-950 grid place-items-center transition-colors duration-500">
              <LifeBuoy className="h-5 w-5" strokeWidth={2} />
            </div>
            <div className="font-heading font-bold text-lg tracking-tight">HelpDesk</div>
          </div>

          {/* Custom district logo — sits between HelpDesk badge and the tagline block,
              left-aligned to match the text formatting above and below */}
          {loginLogo && (
            <div className="flex items-center justify-start flex-1 -my-6" data-testid="login-custom-logo">
              <img
                src={loginLogo}
                alt="District logo"
                className="max-h-56 max-w-full object-contain drop-shadow-[0_8px_24px_rgba(0,0,0,0.25)] dark:drop-shadow-[0_8px_24px_rgba(0,0,0,0.5)] transition-transform duration-500"
              />
            </div>
          )}

          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-600 dark:text-zinc-300 mb-3 transition-colors duration-500">Internal Operations</div>
            <h1 className="font-heading text-4xl lg:text-5xl font-bold leading-tight max-w-md text-zinc-900 dark:text-zinc-50 transition-colors duration-500">
              A single Ticket Queue for IT, Maintenance &amp; Custodians.
            </h1>
            <p className="mt-4 text-sm text-zinc-700 dark:text-zinc-300 max-w-sm leading-relaxed transition-colors duration-500">
              Submit, triage, and resolve. Clarity over clutter — every request tracked from open to closed.
            </p>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 md:p-10 bg-white dark:bg-zinc-950">
        <div className="w-full max-w-sm">
          <div className="mb-8 md:hidden flex items-center gap-2">
            <div className="h-9 w-9 rounded-md bg-zinc-950 text-white grid place-items-center dark:bg-white dark:text-zinc-950">
              <LifeBuoy className="h-5 w-5" />
            </div>
            <div className="font-heading font-bold text-lg text-zinc-950 dark:text-white">HelpDesk</div>
          </div>
          <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 dark:text-zinc-400 mb-2">Sign in</div>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-zinc-950 dark:text-white">Welcome back.</h2>
          <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">Enter your credentials to continue.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Email</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 rounded-md border-zinc-300 focus-visible:ring-2 focus-visible:ring-blue-500/20 focus-visible:border-blue-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Password</Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 rounded-md border-zinc-300 focus-visible:ring-2 focus-visible:ring-blue-500/20 focus-visible:border-blue-500"
              />
            </div>

            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="w-full h-11 rounded-md bg-[#0055FF] hover:bg-[#0040CC] transition-colors duration-200 text-white font-medium"
            >
              {busy ? "Signing in…" : (<>Sign in <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} /></>)}
            </Button>
          </form>

          {ssoEnabled && (
            <div className="mt-6">
              <div className="relative flex items-center my-4">
                <span className="flex-1 h-px bg-zinc-200 dark:bg-zinc-800" />
                <span className="px-3 text-[10px] uppercase tracking-[0.2em] text-zinc-400 dark:text-zinc-500 font-medium">or</span>
                <span className="flex-1 h-px bg-zinc-200 dark:bg-zinc-800" />
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/auth/microsoft/start`}
                data-testid="microsoft-sso-button"
                aria-label="Sign in with Microsoft"
                className="group relative w-full h-12 rounded-md border border-[#8C8C8C] dark:border-zinc-700 bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800 active:bg-zinc-100 dark:active:bg-zinc-800/70 transition-all duration-200 flex items-center justify-center gap-3 px-4 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#0067B8]/40 focus:ring-offset-2 dark:focus:ring-offset-zinc-950"
              >
                {/* Official Microsoft 4-color logo — 21x21 with 1px inner gap per Microsoft brand guidelines */}
                <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden="true" className="flex-shrink-0">
                  <rect x="1"  y="1"  width="9" height="9" fill="#F25022" />
                  <rect x="11" y="1"  width="9" height="9" fill="#7FBA00" />
                  <rect x="1"  y="11" width="9" height="9" fill="#00A4EF" />
                  <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                </svg>
                <span className="text-[15px] font-semibold text-[#5E5E5E] dark:text-zinc-100 tracking-tight" style={{ fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif' }}>
                  Sign in with Microsoft
                </span>
              </a>
              <p className="mt-3 text-center text-xs text-zinc-500 dark:text-zinc-400">
                Use your <span className="font-medium text-zinc-700 dark:text-zinc-300">@esusd.org</span> district account
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
